import { motion } from "framer-motion";

const projects = [
  {
    icon: "🗣️",
    title: "DebateHQ — Debate Platform",
    company: "Aiwoox",
    desc: "End-to-end debate platform with AI-powered practice, real-time one-on-one and team debates via Socket.io, Supabase auth, learning modules, and mobile support via Capacitor.",
    tags: ["Angular 19", "Supabase", "WebRTC", "Docker", "K8s", "GitHub Actions", "VPS"],
    color: "from-sky-500 to-blue-600",
    type: "Production",
  },
  {
    icon: "🏨",
    title: "GoReservee — Hotel Booking",
    company: "Aiwoox",
    desc: "Multi-service travel booking platform for hotels, flights, tours, cabs, and restaurants with real-time availability, AWS Cognito auth, RDS database, and S3 storage.",
    tags: ["Angular 18", "AWS EC2", "RDS", "Cognito", "S3", "Docker", "NGINX", "CI/CD"],
    color: "from-orange-400 to-yellow-500",
    type: "Production",
  },
  {
    icon: "📈",
    title: "Stock Analysis Platform",
    company: "Aiwoox",
    desc: "Next.js trading analytics platform with P&L calculations, win-rate analysis, strategy management, AWS S3 presigned URL uploads, and Firebase real-time data.",
    tags: ["Next.js", "TypeScript", "AWS S3", "EC2", "IAM", "Firebase", "AWS SDK v3"],
    color: "from-purple-500 to-pink-500",
    type: "Production",
  },
  {
    icon: "🔄",
    title: "CI/CD Automation Pipeline",
    company: "Freelance",
    desc: "Fully automated CI/CD pipeline using GitHub Actions and Jenkins with Docker image builds, automated testing, and zero-downtime Kubernetes deployments.",
    tags: ["Jenkins", "GitHub Actions", "Docker", "Kubernetes", "CI/CD"],
    color: "from-green-400 to-emerald-500",
    type: "Freelance",
  },
  {
    icon: "☁️",
    title: "AWS Infrastructure with Terraform",
    company: "Freelance",
    desc: "Provisioned complete AWS infrastructure using Terraform IaC — VPC, EC2, RDS, S3, IAM, ALB, CloudWatch monitoring, and auto-scaling groups.",
    tags: ["Terraform", "AWS", "VPC", "EC2", "RDS", "CloudWatch", "IaC"],
    color: "from-orange-500 to-red-500",
    type: "Freelance",
  },
  {
    icon: "🐳",
    title: "Dockerized App Deployment",
    company: "Freelance",
    desc: "Containerized full-stack applications using Docker Compose with NGINX reverse proxy, SSL termination, environment-based configuration, and VPS deployment.",
    tags: ["Docker", "Docker Compose", "NGINX", "SSL", "VPS", "Linux"],
    color: "from-teal-400 to-cyan-500",
    type: "Freelance",
  },
];

export default function Projects() {
  return (
    <section id="projects" className="py-16 md:py-24 px-4 sm:px-6 bg-[#0d1526]/50">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <p className="text-sky-400 font-semibold uppercase tracking-widest text-sm mb-2">What I've Built</p>
          <h2 className="text-4xl md:text-5xl font-black text-white">Projects</h2>
          <div className="w-16 h-1 bg-sky-500 mx-auto mt-4 rounded-full" />
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((p, i) => (
            <motion.div
              key={p.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              className="bg-[#0a0f1e] border border-sky-500/15 rounded-2xl overflow-hidden card-hover group flex flex-col w-full min-w-0"
            >
              <div className={`h-1.5 bg-gradient-to-r ${p.color}`} />
              <div className="p-6 flex flex-col flex-1">
                <div className="flex items-start justify-between mb-3">
                  <span className="text-3xl">{p.icon}</span>
                  <span className={`text-xs font-semibold px-2 py-1 rounded-full border ${
                    p.type === "Production"
                      ? "bg-green-500/10 border-green-500/30 text-green-400"
                      : "bg-sky-500/10 border-sky-500/30 text-sky-400"
                  }`}>
                    {p.type}
                  </span>
                </div>
                <h3 className="text-white font-bold text-base mb-1 group-hover:text-sky-400 transition-colors">
                  {p.title}
                </h3>
                <p className="text-sky-500 text-xs font-semibold mb-3">{p.company}</p>
                <p className="text-slate-400 text-sm leading-relaxed mb-4 flex-1">{p.desc}</p>
                <div className="flex flex-wrap gap-1.5">
                  {p.tags.map((t) => (
                    <span key={t} className="bg-slate-800 text-slate-300 text-xs px-2 py-0.5 rounded-md border border-slate-700/50">
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
